import { useState } from 'react';
import UserAvatar from './UserAvatar';
import SprintItemModal from './SprintItemModal';
import AddItemModal from './AddItemModal';
import BacklogTreeItem from './BacklogTreeItem';
import ConfirmModal from './ConfirmModal';
import CustomSelect from './CustomSelect';
import { ChevronLeftIcon, ChevronRightIcon, LoaderIcon, InboxIcon, HistoryIcon, PlusIcon, CopyIcon } from './Icons';
import { TYPE_CONFIG, STATUS_LABELS, LOCATION_OPTIONS, PRIORITY_CONFIG } from '../constants';
import { formatDate, findAssignee, buildItemTree, getAllowedChildTypes, isContainerType } from '../utils';
import useBacklogHistory from '../hooks/useBacklogHistory';

export default function BacklogView({ items, onBack, onUpdateItem, onDeleteItem, onAddItem, showToast, users, userProfile, projectId, sprint, isKanban }) {
  const [selectedItem, setSelectedItem] = useState(null);
  const [showAddModal, setShowAddModal] = useState(false);
  const [showHistory, setShowHistory] = useState(false);
  const [expandedIds, setExpandedIds] = useState({});
  const [addParentContext, setAddParentContext] = useState(null);
  const [deleteTarget, setDeleteTarget] = useState(null);

  const { historySprints, loadingHistory, expandedSprints, toggleSprint } = useBacklogHistory({ showToast, projectId, showHistory });

  const totalPoints = items.reduce((sum, i) => sum + (i.storyPoints || 0), 0);

  const handleLocationChange = async (itemId, newLocation) => {
    try {
      await onUpdateItem(itemId, { location: newLocation });
      showToast(newLocation === 'board' ? 'Item moved to board' : 'Item moved to backlog', 'success');
    } catch (err) {
      showToast(err.message || 'Failed to move item', 'error');
    }
  };

  const handleAddBacklogItem = async (itemData) => {
    await onAddItem({ ...itemData, location: 'backlog' });
  };

  const handleToggle = (id) => {
    setExpandedIds((prev) => ({ ...prev, [id]: !prev[id] }));
  };

  const handleTreeDelete = (node) => {
    const childCount = isContainerType(node.type)
      ? items.filter((i) => i.parentId === node.id).length
      : 0;
    setDeleteTarget({ ...node, childCount });
  };

  const confirmTreeDelete = async () => {
    if (!deleteTarget) return;
    try {
      await onDeleteItem(deleteTarget.id);
      setDeleteTarget(null);
      showToast('Item deleted', 'success');
    } catch (err) {
      showToast(err.message || 'Failed to delete item', 'error');
    }
  };

  const handleAddChild = (parentNode) => {
    setAddParentContext(parentNode);
    setShowAddModal(true);
  };

  const handleOpenAddModal = () => {
    setAddParentContext(null);
    setShowAddModal(true);
  };

  if (showHistory) {
    return (
      <div className="backlog-wrapper">
        <div className="backlog-header">
          <button className="btn btn-ghost btn-sm" onClick={() => setShowHistory(false)}>
            <ChevronLeftIcon size={16} /> Back
          </button>
          <h2><HistoryIcon size={20} /> Backlog History</h2>
        </div>

        {loadingHistory ? (
          <div className="app-loading">
            <LoaderIcon size={24} />
            <p>Loading history...</p>
          </div>
        ) : historySprints.length === 0 ? (
          <div className="backlog-empty">
            No completed items found in closed sprints.
          </div>
        ) : (
          <div className="backlog-history-list">
            {historySprints.map(({ sprint: s, items: sItems }) => {
              const isExpanded = expandedSprints[s.id];
              const doneCount = sItems.filter((i) => i.status === 'done').length;
              return (
                <div key={s.id} className="backlog-history-section">
                  <div className="backlog-history-section-header" onClick={() => toggleSprint(s.id)}>
                    <ChevronRightIcon size={16} className={`backlog-history-chevron${isExpanded ? ' backlog-history-chevron--open' : ''}`} />
                    <h3>{s.name}</h3>
                    <span className="backlog-history-dates">
                      {formatDate(s.startDate)} &ndash; {formatDate(s.endDate)}
                    </span>
                    <span className="backlog-history-count">{doneCount} completed &middot; {sItems.length} total</span>
                  </div>
                  {isExpanded && (
                    <div className="backlog-history-items">
                      {sItems.map((item) => {
                        const config = TYPE_CONFIG[item.type] || TYPE_CONFIG.task;
                        const Icon = config.icon;
                        const assignee = findAssignee(users, item.assigneeId);
                        const statusLabel = STATUS_LABELS[item.status] || 'Not Started';
                        return (
                          <div key={item.id} className="backlog-item" style={{ cursor: 'default' }}>
                            <div className="backlog-item-type" style={{ borderColor: config.color }}>
                              <Icon size={16} style={{ color: config.color }} />
                            </div>
                            <div className="backlog-item-info">
                              {item.readableId && (
                                <span className="sprint-item-rid">{item.readableId}</span>
                              )}
                              <span className="backlog-item-title">{item.title}</span>
                              {item.description && (
                                <span className="backlog-item-desc">{item.description}</span>
                              )}
                            </div>
                            <div className="backlog-item-meta">
                              {assignee && (
                                <div className="backlog-history-assignee">
                                  <UserAvatar user={assignee} size={28} showStatus={false} />
                                  <span className="backlog-history-assignee-name">{assignee.displayName}</span>
                                </div>
                              )}
                              {item.storyPoints != null && (
                                <span className="sprint-item-points">{item.storyPoints}</span>
                              )}
                              <span className={`status-badge status-${item.status}`}>
                                {statusLabel}
                              </span>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>
    );
  }

  // Scrum mode: render ALL items in hierarchy tree (items on board stay in their parent position)
  const tree = !isKanban ? buildItemTree(items) : [];

  return (
    <div className="backlog-wrapper">
      <div className="backlog-header">
        <button className="btn btn-ghost btn-sm" onClick={onBack}>
          <ChevronLeftIcon size={16} /> Back to Board
        </button>
        <h2><InboxIcon size={20} /> Backlog</h2>
        <div className="backlog-header-actions">
          <button className="btn btn-ghost btn-sm" onClick={() => setShowHistory(true)}>
            <HistoryIcon size={16} /> History
          </button>
          <button className="btn btn-primary btn-sm" onClick={handleOpenAddModal}>
            <PlusIcon size={16} /> Add Item
          </button>
        </div>
      </div>

      <div className="backlog-toolbar">
        <span>{items.length} item{items.length !== 1 ? 's' : ''}</span>
        <span>&middot;</span>
        <span>{totalPoints} pts</span>
      </div>

      {items.length === 0 ? (
        <div className="backlog-empty">
          No items yet. Items moved from sprints or added here will appear in this list.
        </div>
      ) : !isKanban ? (
        <div className="backlog-list">
          {/* Scrum: render full hierarchy tree */}
          {tree.map((node) => (
            <BacklogTreeItem
              key={node.id}
              node={node}
              depth={0}
              expandedIds={expandedIds}
              onToggle={handleToggle}
              onClickItem={setSelectedItem}
              onAddChild={handleAddChild}
              onLocationChange={handleLocationChange}
              onDeleteItem={handleTreeDelete}
              users={users}
              showToast={showToast}
            />
          ))}
        </div>
      ) : (
        <div className="backlog-list">
          {/* Kanban: flat list sorted by priority */}
          {[...items].sort((a, b) => (PRIORITY_CONFIG[a.priority]?.order ?? 1) - (PRIORITY_CONFIG[b.priority]?.order ?? 1)).map((item) => {
            const config = TYPE_CONFIG[item.type] || TYPE_CONFIG.task;
            const Icon = config.icon;
            const assignee = findAssignee(users, item.assigneeId);
            const isOnBoard = !item.location || item.location === 'board';
            const statusLabel = STATUS_LABELS[item.status] || 'Not Started';
            const priorityColor = item.priority ? PRIORITY_CONFIG[item.priority]?.color : null;
            return (
              <div
                key={item.id}
                className={`backlog-item${isOnBoard ? ' backlog-item--on-board' : ''}${priorityColor ? ' backlog-item--priority' : ''}`}
                style={priorityColor ? {
                  background: `${priorityColor}18`,
                  borderColor: `${priorityColor}40`,
                  borderLeftColor: priorityColor,
                  borderLeftWidth: '3px',
                } : undefined}
                onClick={() => setSelectedItem(item)}
              >
                <div className="backlog-item-type" style={{ borderColor: config.color }}>
                  <Icon size={16} style={{ color: config.color }} />
                </div>
                <div className="backlog-item-info">
                  <div className="backlog-item-info-top">
                    {item.readableId && (
                      <span
                        className="backlog-item-rid"
                        onClick={(e) => {
                          e.stopPropagation();
                          navigator.clipboard.writeText(`${item.readableId}-${item.title}`);
                          showToast('ID copied', 'success');
                        }}
                      >
                        {item.readableId} <CopyIcon size={10} />
                      </span>
                    )}
                    {item.priority && (
                      <span className="backlog-priority-dot" style={{ background: priorityColor }}>{PRIORITY_CONFIG[item.priority]?.label}</span>
                    )}
                  </div>
                  <span className="backlog-item-title">{item.title}</span>
                  {item.description && (
                    <span className="backlog-item-desc">{item.description}</span>
                  )}
                </div>
                <div className="backlog-item-meta">
                  {assignee && (
                    <UserAvatar user={assignee} size={28} showStatus={false} />
                  )}
                  {item.storyPoints != null && (
                    <span className="sprint-item-points">{item.storyPoints}</span>
                  )}
                  <span className={`status-badge status-${item.status}`}>
                    {statusLabel}
                  </span>
                  {isOnBoard && (
                    <span className="backlog-on-board-badge">On Board</span>
                  )}
                  <div onClick={(e) => e.stopPropagation()}>
                    <CustomSelect
                      value={item.location || 'board'}
                      onChange={(val) => handleLocationChange(item.id, val)}
                      options={LOCATION_OPTIONS}
                      size="sm"
                      compact
                    />
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {selectedItem && (
        <SprintItemModal
          item={selectedItem}
          onClose={() => setSelectedItem(null)}
          onUpdate={onUpdateItem}
          onDelete={onDeleteItem}
          users={users}
          isKanban={isKanban}
          allItems={items}
          initialEditing
        />
      )}

      {showAddModal && (
        <AddItemModal
          onClose={() => { setShowAddModal(false); setAddParentContext(null); }}
          onAdd={handleAddBacklogItem}
          users={users}
          isKanban={isKanban}
          parentId={addParentContext?.id || null}
          allowedTypes={addParentContext ? getAllowedChildTypes(addParentContext.type) : null}
          parentContext={addParentContext}
        />
      )}

      {deleteTarget && (
        <ConfirmModal
          message={`Delete "${deleteTarget.title}"?${deleteTarget.childCount > 0 ? ` This will also delete ${deleteTarget.childCount} child item${deleteTarget.childCount > 1 ? 's' : ''}.` : ''} This action cannot be undone.`}
          onConfirm={confirmTreeDelete}
          onCancel={() => setDeleteTarget(null)}
        />
      )}
    </div>
  );
}
